import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { z } from "zod";

import { Diff } from "@/features/FormCard/Diff";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
  SelectField,
  SelectFieldWithSection,
  TextareaField,
} from "@/features/FormCard/Fields";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import {
  dateSchema,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseToDateValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { ExternalLink } from "@/components/ExternalLink";
import { FixingMode } from "@/data/fixingMode";
import { useEditForm } from "@/features/Edit/hooks";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { useEffect, useMemo, useRef } from "react";
import { useCategoryAutoSelection } from "./hook";
import { fieldTranslations } from "./translations";
import {
  deriveLocalCurrency,
  getKeyByTranslation,
  isCashAccount,
  isCashFormat,
  isSecuritiesAccount,
} from "./utils";
import { AUTOMATIC_TAG_MAP } from "./config";
import { computeFixingMode } from "../N1TimeSeries/utils";
import { CashFeeContent, SecuritiesFeeContent } from "@/components/Form/FeesEdit";
import i18n from "@/i18n";

const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),

  n1_portfolio_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        account_type: z.string(),
        currency: z.string(),
      }),
    ]),
  ),
  n1_static_datum_name: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      // we recv / send as string the data
      z.string(),

      // for the autocomplete, the form state is an object
      z.object({
        text: z.string(),
        id: z.number(),
        asset_class_2: z.string(),
        asset_class_1: z.string(),
        currency: z.string(),
        reporting_currency: z.string(),
      }),
    ]),
  ),
  n1_portfolio_reporting_currency: z.string().nullable().optional(),
  n1_portfolio_account_type: z.string().nullable().optional(),
  n1_static_datum_asset_class_1: z.string().nullable().optional(),
  n1_static_datum_asset_class_2: z.string().nullable().optional(),
  n1_portfolio_id: z.number(),
  n1_static_datum_id: z.number(),
  quantity: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number(),
  category: z.string().nullable().optional(),
  transfer: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  fee_currency: z.string().nullable().optional(),
  broker_fee: z.coerce.number().nullable().optional(),
  custodian_broker_fee: z.coerce.number().nullable().optional(),
  other_fee: z.coerce.number().nullable().optional(),
  fees: z.coerce.number().nullable().optional(),
  fee_type: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  time_serie_file_id: z.number().nullable().optional(),
  n1_time_serie_file_id: z.number().nullable().optional(),
  n1_portfolio_currency: z.string().nullable().optional(),
  n1_static_datum_currency: z.string().nullable().optional(),
});
const formSchemaFields = FormSchema.keyof().enum;

const autocompleteFields = {
  client: "client",
  bank_name: "bank_name",
} as const;

export type EditFormSchemaFields = typeof formSchemaFields;

export const formFields = {
  ...formSchemaFields,
  ...autocompleteFields,
};

type BaseForm = z.infer<typeof FormSchema>;

export type TForm = Omit<
  BaseForm,
  "n1_portfolio_name" | "n1_static_datum_name"
> & {
  n1_portfolio_name: Extract<BaseForm["n1_portfolio_name"], { id: number }>;
  n1_static_datum_name: Extract<
    BaseForm["n1_static_datum_name"],
    { id: number }
  >;
};

export function N1TransactionsEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
    translations,
    isLoadingData,
  } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
    fieldsNotApplyTranslation: [formFields.transfer, formFields.fee_type],
    adjustOnReset: (key, value, fetchedData) => {
      if (key === formFields.n1_portfolio_name) {
        return {
          text: value,
          id: fetchedData.n1_portfolio_id,
          account_type: fetchedData.n1_portfolio_account_type,
          currency: fetchedData.n1_portfolio_currency,
          reporting_currency: fetchedData.n1_portfolio_reporting_currency,
        };
      } else if (key === formFields.n1_static_datum_name) {
        return {
          text: value,
          id: fetchedData.n1_static_datum_id,
          asset_class_2: fetchedData.n1_static_datum_asset_class_2,
          asset_class_1: fetchedData.n1_static_datum_asset_class_1,
          currency: fetchedData.n1_static_datum_currency,
        };
      }
      return value;
    },
  });

  const currencyOptions = useCurrencyOptions();

  const { movementsSections, isCategoryDisabled } = useCategoryAutoSelection({
    formFields,
    form,
    translations,
    keysAllowNullValue: [formFields.fees],
    isLoadingData,
  });


  const portfolio = form.watch(formFields.n1_portfolio_name);
  const portfolioId = portfolio?.id;
  const portfolioType = portfolio?.account_type;
  const portfolioCurrency = portfolio?.currency;
  const reportingCurrency = portfolio?.reporting_currency;

  const asset = form.watch(formFields.n1_static_datum_name);
  const assetId = asset?.id;
  const assetCurrency = asset?.currency;
  const assetClass2 = asset?.asset_class_2 ?? null;

  const client_name = form.getValues(formFields.client)?.text ?? "";
  const bank_name = form.getValues(formFields.bank_name)?.text ?? "";

  const category = form.watch(formFields.category);
  const marketValueLocal = form.watch(formFields.market_value_local_ccy);
  const transferAutoRef = useRef(false);

  const localCurrency = useMemo(
    () =>
      deriveLocalCurrency(
        portfolioType ?? null,
        portfolioCurrency,
        assetCurrency,
      ),
    [portfolioType, portfolioCurrency, assetCurrency],
  );

  const isTypeCash = isCashAccount(portfolioType);
  const isTypeSecurities = isSecuritiesAccount(portfolioType);

  const isCategoryFees =
    getKeyByTranslation(category, translations?.[formFields.category]) ===
    formFields.fees;
  const isCategoriesEmpty = movementsSections.length == 0;

  const isCashAsset = useMemo(() => isCashFormat(asset?.text), [asset]);
  const fixingMode: FixingMode | null = useMemo(
    () => computeFixingMode(assetClass2),
    [assetClass2],
  );

  const changesArmedRef = useRef(false);
  useEffect(() => {
    const sub = form.watch((_values, info) => {
      // arm on any meaningful change
      if (
        info?.name === formFields.n1_static_datum_name ||
        info?.name === formFields.n1_portfolio_name ||
        info?.name === formFields.market_value_local_ccy
      ) {
        changesArmedRef.current = true;
      }
    });
    return () => sub?.unsubscribe?.();
  }, [form]);

  const hadInitialQuantityRef = useRef(
    form.getValues(formFields.quantity) !== null &&
      form.getValues(formFields.quantity) !== undefined,
  );
  const isShowQuantity = isTypeSecurities && fixingMode === FixingMode.FIXING;
  const showQuantityField = changesArmedRef.current
    ? isShowQuantity
    : hadInitialQuantityRef.current || isShowQuantity;

  const amountSign = useMemo(() => {
    const n = Number(marketValueLocal);
    return isNaN(n) ? 0 : n === 0 ? 0 : n > 0 ? 1 : -1;
  }, [marketValueLocal]);

  useEffect(() => {
    if (!AUTOMATIC_TAG_MAP.includes(assetClass2)) {
      if (transferAutoRef.current) {
        form.setValue(formFields.transfer, null, { shouldDirty: true });
        transferAutoRef.current = false;
      }
      return;
    }
    // compute the desired label from the sign
    const nextLabel =
      amountSign > 0 ? "deposit" : amountSign < 0 ? "withdrawal" : "";
    const current = form.getValues(formFields.transfer);

    // no label when amount is 0/blank: clear only if we set it
    if (!nextLabel) {
      if (transferAutoRef.current) {
        form.setValue(formFields.transfer, null, { shouldDirty: true });
        transferAutoRef.current = false;
      }
      return;
    }
    // update whenever the derived label differs
    if (current !== nextLabel) {
      form.setValue(formFields.transfer, nextLabel, { shouldDirty: true });
      transferAutoRef.current = true; // mark as auto-set
    }
  }, [assetClass2, marketValueLocal, form]);

  // Clear quantity when it becomes hidden (prevents stale values)
  useEffect(() => {
    if (changesArmedRef.current && !showQuantityField) {
      form.setValue(formFields.quantity, null, { shouldDirty: true });
    }
  }, [showQuantityField, form]);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("transactions.transaction")} {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formFields.date}
                    label={t("transactions.transaction_date")}
                    isRequired={true}
                    control={form.control}
                  />
                </div>
                <AutocompleteAsyncField
                  name={formFields.n1_static_datum_name}
                  label={t("transactions.asset")}
                  isRequired={true}
                  control={form.control}
                  base_url={`${Clients.N1}/${Tables.N1_STATIC_DATA}`}
                  id_column={formFields.n1_static_datum_id}
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: `${Clients.N1}`,
                    table_name: `${Tables.N1_STATIC_DATA}`,
                    language: i18n.resolvedLanguage?.startsWith("fr")
                      ? "fr"
                      : "en",
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name={formFields.client}
                    label={t("transactions.client")}
                    control={form.control}
                    base_url={`${Clients.N1}/${Tables.N1_PORTFOLIOS}`}
                    id_column={formFields.n1_portfolio_id}
                    column_name={formFields.client}
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: true,
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (bank_name) equals.bank_name = bank_name;
                        if (portfolioId) equals.id = portfolioId;
                        return { equals };
                      },
                    }}
                  />
                  <AutocompleteAsyncField
                    name={formFields.bank_name}
                    label={t("transactions.bank")}
                    control={form.control}
                    base_url={`${Clients.N1}/${Tables.N1_PORTFOLIOS}`}
                    id_column={formFields.n1_portfolio_id}
                    column_name={formFields.bank_name}
                    showId={false}
                    isRequired
                    options={{
                      autoSelectSingle: true,
                      filters: () => {
                        const equals: Record<string, string> = {};
                        if (client_name) equals.client = client_name;
                        if (portfolioId) equals.id = portfolioId;
                        return { equals };
                      },
                    }}
                  />
                </div>
                <AutocompleteAsyncField
                  name={formFields.n1_portfolio_name}
                  label={t("transactions.account")}
                  isRequired={true}
                  control={form.control}
                  base_url={`${Clients.N1}/${Tables.N1_PORTFOLIOS}`}
                  id_column={formFields.n1_portfolio_id}
                  column_name="name"
                  options={{
                    filters: () => {
                      const equals: Record<string, string> = {};
                      if (client_name) equals.client = client_name;
                      if (portfolioId) equals.id = portfolioId;
                      return { equals };
                    },
                  }}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    isRequired={true}
                    name={formFields.market_value_local_ccy}
                    label={t("transactions.amount_local")}
                    type="number"
                    control={form.control}
                    endContent={
                      <div className="opacity-50 text-xs">{localCurrency}</div>
                    }
                  />
                  <InputField
                    name={formFields.market_value_ptf_ccy}
                    label={t("transactions.amount_reporting")}
                    control={form.control}
                    endContent={
                      <div className="opacity-50 text-xs">
                        {reportingCurrency}
                      </div>
                    }
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formFields.quantity}
                    label={t("transactions.quantity")}
                    control={form.control}
                    type="number"
                    isRequired={showQuantityField}
                    fieldOptions={{
                      isDisabled: !showQuantityField,
                      changeOnDisable: false,
                    }}
                  />
                  <SelectField
                    name={formFields.transfer}
                    label={t("transactions.transfer_type")}
                    options={translations?.["transfer"]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectFieldWithSection
                    allow_empty_option={isCashAsset}
                    label={t("transactions.movement_type")}
                    name={formFields.category}
                    sections={movementsSections}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: isCategoryDisabled,
                      isVisible: !isCategoriesEmpty,
                    }}
                  />
                </div>
                <div className="overflow-hidden">
                  {isTypeCash && isCashAsset && (
                    <CashFeeContent
                      form={form}
                      isDisabled={!isCashAsset || !isCategoryFees}
                      feeOptionsTranslation={translations?.["fee_type"]}
                    />
                  )}
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name={formFields.description}
                    label={t("transactions.movement_description")}
                    control={form.control}
                  />
                </div>
                <div className="max-w-full bg-transparent text-xs">
                  {isTypeSecurities && (
                    <div className="py-2">
                      <Divider />
                      <SecuritiesFeeContent
                        form={form}
                        currencyOptions={currencyOptions}
                      />
                    </div>
                  )}
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={true}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <div className="absolute top-12 right-12 2xl:top-10 2xl:right-10 md:flex gap-2 flex-row hidden">
          {assetId && (
            <Button
              href={toWorkspacePath(`/admin/n1/n1_static_data/${assetId}`)}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Actif
            </Button>
          )}
          {portfolioId && (
            <Button
              href={toWorkspacePath(`/admin/n1/n1_portfolios/${portfolioId}`)}
              variant="bordered"
              color="primary"
              showAnchorIcon
              as={ExternalLink}
              radius="full"
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              Compte
            </Button>
          )}
        </div>
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? Diff(
                  changedFields,
                  [formFields.n1_portfolio_id, formFields.n1_static_datum_id],
                  fieldTranslations,
                )
              : t("no_changes")
          }
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          isLoading={isDeleting}
          requireTyping
        />
      </div>
    </div>
  );
}
